import { createReducer, on } from '@ngrx/store';
import { ReportActions } from './report.actions';
import { ExpenseReport, NRTReport, Report } from '@core/interface/report';
import { Pagination } from '@core/interface/core';

export const ReportFeatureKey = 'report';

export interface ReportState {
  reports: {
    data: Report[];
    pagination: Pagination | null;
    total_hours: number;
    total_claim_hours: string;
    total_claim_ot_1_5_hours: string;
    total_claim_ot_2_0_hours: string;
  };
  NRTreports: {
    data: NRTReport[];
    pagination: Pagination | null;
    total_hours: number;
    count: number;
    isLoading: boolean;
    isExcelLoading: boolean;
  };
  expenseReports: {
    data: ExpenseReport[];
    pagination: Pagination | null;
    count: number;
    isLoading: boolean;
    isExcelLoading: boolean;
  };
  isLoading: boolean;
  isNoteUpdating: boolean;
  isExcelLoading: boolean;
}

export const initialState: ReportState = {
  reports: {
    data: [],
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0,
    },
    total_hours: 0,
    total_claim_hours: '0',
    total_claim_ot_1_5_hours: '0',
    total_claim_ot_2_0_hours: '0',
  },
  NRTreports: {
    data: [],
    pagination: null,
    total_hours: 0,
    count: 0,
    isLoading: false,
    isExcelLoading: false,
  },
  expenseReports: {
    data: [],
    pagination: null,
    count: 0,
    isLoading: false,
    isExcelLoading: false,
  },
  isLoading: false,
  isNoteUpdating: false,
  isExcelLoading: false,
};

export const ReportReducer = createReducer(
  initialState,
  on(ReportActions.loadReport, (state) => ({
    ...state,
    isLoading: true,
    reports: {
      ...state.reports,
      data: [],
      total_hours: 0,
      total_claim_hours: '0',
      total_claim_ot_1_5_hours: '0',
      total_claim_ot_2_0_hours: '0',
    },
  })),
  on(ReportActions.loadReportSuccess, (state, data) => ({
    ...state,
    reports: {
      data: data.data,
      pagination: data.pagination,
      total_hours: data.total_hours,
      total_claim_hours: data.total_claim_hours,
      total_claim_ot_1_5_hours: data.total_claim_ot_1_5_hours,
      total_claim_ot_2_0_hours: data.total_claim_ot_2_0_hours,
    },
    isLoading: false,
  })),
  on(ReportActions.loadReportFailure, (state) => {
    return {
      ...state,
      isLoading: false,
      reports: {
        data: [],
        pagination: null,
        total_hours: 0,
        total_claim_hours: '0',
        total_claim_ot_1_5_hours: '0',
        total_claim_ot_2_0_hours: '0',
      },
    };
  }),
  on(ReportActions.updateNote, (state) => ({ ...state, isNoteUpdating: true })),
  on(ReportActions.updateNoteSuccess, (state, { data }) => ({
    ...state,
    isNoteUpdating: false,
    reports: {
      ...state.reports,
      data: state.reports.data.map((report) =>
        data.timesheet_id == report.timesheet_id ? data : report
      ),
    },
  })),
  on(ReportActions.updateNoteFailure, (state) => ({
    ...state,
    isNoteUpdating: false,
  })),
  on(ReportActions.resetReports, (state) => ({
    ...state,
    reports: {
      data: [],
      pagination: null,
      total_hours: 0,
      total_claim_hours: '0',
      total_claim_ot_1_5_hours: '0',
      total_claim_ot_2_0_hours: '0',
    },
    NRTreports: {
      ...state.NRTreports,
      data: [],
      count: 0,
      total_hours: 0,
      pagination: null,
    },
    expenseReports: {
      ...state.expenseReports,
      data: [],
      count: 0,
      pagination: null,
    },
  })),

  on(ReportActions.exportExcel, (state) => ({
    ...state,
    isExcelLoading: true,
  })),
  on(ReportActions.exportExcelSuccess, (state) => ({
    ...state,
    isExcelLoading: false,
  })),
  on(ReportActions.exportExcelFailure, (state) => ({
    ...state,
    isExcelLoading: false,
  })),

  on(ReportActions.loadNRTReport, (state) => ({
    ...state,
    NRTreports: {
      ...state.NRTreports,
      data: [],
      total_hours: 0,
      count: 0,
      isLoading: true,
    },
  })),
  on(ReportActions.loadNRTReportSuccess, (state, data) => ({
    ...state,
    NRTreports: {
      ...state.NRTreports,
      data: data.data,
      pagination: data.pagination,
      total_hours: data.total_hours,
      count: data.count,
      isLoading: false,
    },
  })),
  on(ReportActions.loadNRTReportFailure, (state) => {
    return {
      ...state,
      NRTreports: {
        ...state.NRTreports,
        data: [],
        pagination: null,
        total_hours: 0,
        count: 0,
        isLoading: false,
      },
    };
  }),

  on(ReportActions.exportNRTExcel, (state) => ({
    ...state,
    NRTreports: {
      ...state.NRTreports,
      isExcelLoading: true,
    },
  })),
  on(ReportActions.exportNRTExcelSuccess, (state) => ({
    ...state,
    NRTreports: {
      ...state.NRTreports,
      isExcelLoading: false,
    },
  })),
  on(ReportActions.exportNRTExcelFailure, (state) => ({
    ...state,
    NRTreports: {
      ...state.NRTreports,
      isExcelLoading: false,
    },
  })),

  on(ReportActions.loadExpenseReport, (state) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      data: [],
      count: 0,
      isLoading: true,
    },
  })),
  on(ReportActions.loadExpenseReportSuccess, (state, data) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      data: data.data,
      pagination: data.pagination,
      count: data.count,
      isLoading: false,
    },
  })),
  on(ReportActions.loadExpenseReportFailure, (state) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      data: [],
      pagination: null,
      count: 0,
      isLoading: false,
    },
  })),

  on(ReportActions.exportExpenseExcel, (state) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      isExcelLoading: true,
    },
  })),
  on(ReportActions.exportExpenseExcelSuccess, (state) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      isExcelLoading: false,
    },
  })),
  on(ReportActions.exportExpenseExcelFailure, (state) => ({
    ...state,
    expenseReports: {
      ...state.expenseReports,
      isExcelLoading: false,
    },
  }))
);
